import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, breakpoints, spacing, themeLight } from '@naf/theme';
import { IconButton } from '@naf/icon-button';
import { propValidationConfig } from './propValidationConfig';
export const FlyoutModal = /*#__PURE__*/React.memo(({
  children,
  isOpen = false,
  maxWidth = 400,
  handleClose,
  title,
  headerZIndex = 1,
  trapFocus = true,
  className
}) => {
  const [mouseDown, setMouseDown] = useState(false);
  useEffect(() => {
    if (isOpen && trapFocus) {
      const element = document.querySelector('#flyout');
      if (element) {
        let focusables = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]');
        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        firstFocusable.focus();
        const trapFocus = e => {
          if (e.key === 'Tab') {
            if (e.shiftKey && document.activeElement === firstFocusable) {
              /* shift + tab */
              lastFocusable.focus();
              e.preventDefault();
            } else if (document.activeElement === lastFocusable) {
              /* tab */
              firstFocusable.focus();
              e.preventDefault();
            }
          }
        };
        document.addEventListener('keydown', trapFocus);
        return () => document.removeEventListener('keydown', trapFocus);
      }
      return () => {};
    }
    return () => {};
  }, [isOpen, trapFocus]);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', removeOutline);
    return () => window.removeEventListener('mousedown', removeOutline);
  }, []);
  const close = useCallback(e => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);
  useEffect(() => {
    window.addEventListener('keydown', e => {
      close(e);
      addOutline(e);
    });
    return () => window.removeEventListener('keydown', e => {
      close(e);
      addOutline(e);
    });
  }, [close]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    onClick: () => handleClose()
  }), /*#__PURE__*/React.createElement(ContentWrapper, {
    id: "flyout"
  }, /*#__PURE__*/React.createElement(Container, {
    maxWidth: maxWidth,
    className: className,
    id: mouseDown ? 'mousedown' : '',
    "aria-modal": isOpen
  }, /*#__PURE__*/React.createElement(Header, {
    headerZIndex: headerZIndex
  }, /*#__PURE__*/React.createElement(StyledIconButton, {
    icon: "close",
    onClick: () => handleClose(),
    ariaLabel: "Lukk",
    variant: "neutral"
  })), /*#__PURE__*/React.createElement(Inner, null, title && /*#__PURE__*/React.createElement(Title, null, title), children)))));
});
export default FlyoutModal;
const ContentWrapper = styled.div.withConfig({
  displayName: "flyout-modal__ContentWrapper",
  componentId: "sc-16i1zv5-0"
})([""]);
const Container = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "flyout-modal__Container",
  componentId: "sc-16i1zv5-1"
})(["background:", ";height:100vh;min-height:500px;width:100%;right:0;position:fixed;top:0;z-index:999;box-shadow:0 0 4px 0 rgba(0,0,0,0.15);overflow-y:scroll;animation:x 0.25s;@keyframes x{from{transform:translateX(100%);}to{transform:translateX(0%);}}", ";@media (max-width:", "){max-width:100%;}&#mousedown *:focus{outline:0;}"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  maxWidth
}) => maxWidth && css(["max-width:", "px;"], maxWidth), breakpoints.s);
const Inner = styled.div.withConfig({
  displayName: "flyout-modal__Inner",
  componentId: "sc-16i1zv5-2"
})(["padding:", ";padding-top:0;color:", ";@media (max-width:", "){padding:", ";padding-top:0;}"], spacing.space48, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, breakpoints.s, spacing.space24);
const Header = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "flyout-modal__Header",
  componentId: "sc-16i1zv5-3"
})(["display:flex;align-items:center;justify-content:flex-end;position:sticky;top:0;", ";"], ({
  headerZIndex
}) => headerZIndex && css(["z-index:", ";"], headerZIndex));
const StyledIconButton = styled(IconButton).withConfig({
  displayName: "flyout-modal__StyledIconButton",
  componentId: "sc-16i1zv5-4"
})(["margin-right:", ";margin-top:", ";margin-bottom:", ";@media (max-width:", "){margin-right:", ";margin-top:", ";margin-bottom:", ";}"], spacing.space32, spacing.space24, spacing.space24, breakpoints.s, spacing.space16, spacing.space16, spacing.space16);
const Overlay = styled.div.withConfig({
  displayName: "flyout-modal__Overlay",
  componentId: "sc-16i1zv5-5"
})(["position:fixed;top:0;left:0;width:100%;height:100%;background-color:", ";z-index:99;animation:opacity 0.3s;animation-fill-mode:forwards;@keyframes opacity{from{opacity:0;}to{opacity:0.5;}}"], themeLight.background.brandInverted);
const Title = styled.h2.withConfig({
  displayName: "flyout-modal__Title",
  componentId: "sc-16i1zv5-6"
})(["margin:0;margin-top:", ";margin-bottom:", ";", ";@media (max-width:", "){margin-top:", ";}"], spacing.space48, spacing.space32, fontStyle.headers.header2, breakpoints.s, spacing.space24);
//# sourceMappingURL=flyout-modal.js.map